import { useState, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthLayout, Input, Button, HiyaLogo } from "@hiya/ui";
import { confirmSignInWithNewPassword } from "../../auth/auth-service";

const PASSWORD_RULES = [
  { test: (p: string) => p.length >= 8, label: "At least 8 characters" },
  { test: (p: string) => /[A-Z]/.test(p), label: "One uppercase letter" },
  { test: (p: string) => /[a-z]/.test(p), label: "One lowercase letter" },
  { test: (p: string) => /\d/.test(p), label: "One number" },
  { test: (p: string) => /[^A-Za-z0-9]/.test(p), label: "One special character" },
];

interface LocationState {
  email?: string;
  missingAttributes?: string[];
}

export function ForcePasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { email = "", missingAttributes = [] } = (location.state as LocationState) ?? {};
  const needsName = missingAttributes.includes("given_name") || missingAttributes.includes("family_name");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const failedRules = PASSWORD_RULES.filter((r) => !r.test(password));
  const passwordsMatch = password === confirm;
  const nameValid = !needsName || fullName.trim().length > 0;
  const isValid = failedRules.length === 0 && passwordsMatch && password.length > 0 && nameValid;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    setError("");
    setLoading(true);
    try {
      const attrs: Record<string, string> = {};
      if (needsName) {
        const parts = fullName.trim().split(/\s+/);
        const first = parts[0] ?? "";
        attrs.given_name = first;
        attrs.family_name = parts.slice(1).join(" ") || first;
      }
      const result = await confirmSignInWithNewPassword(
        password,
        Object.keys(attrs).length > 0 ? attrs : undefined,
      );
      if (result.nextStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
        navigate("/login/otp", { state: { email } });
      } else if (result.nextStep === "DONE") {
        navigate("/dashboard", { replace: true });
      }
    } catch {
      setError("Failed to set password. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title="Set New Password"
      subtitle="Please create a new password for your account."
      icon={<HiyaLogo />}
      backTo="/login"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-8 w-full">
        <div className="flex flex-col gap-2 w-full">
          {needsName && (
            <Input
              type="text"
              placeholder="Full name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          )}
          <Input
            type="password"
            placeholder="New password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Input
            type="password"
            placeholder="Confirm password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            error={confirm && !passwordsMatch ? "Passwords do not match" : undefined}
          />
          {password && failedRules.length > 0 && (
            <ul className="text-xs text-input-hint-error list-disc pl-4">
              {failedRules.map((r) => (
                <li key={r.label}>{r.label}</li>
              ))}
            </ul>
          )}
        </div>
        {error && <p className="text-xs text-input-hint-error text-center">{error}</p>}
        <Button type="submit" disabled={!isValid || loading}>
          Set password
        </Button>
      </form>
    </AuthLayout>
  );
}
